import { ApiClient, PagedResults, ServiceResponse, ServiceResponseFactory, ServiceResponseNoContent } from "../../../common";
import { PlatformSettings } from "../../../settings/platformSettings";
import CreateRoleDto from "../../dtos/in/createRoleDto";
import AppDto from "../../dtos/out/appDto";
import RoleDto from "../../dtos/out/roleDto";
import UserDto from "../../dtos/out/userDto";
import { IRoles } from "../interfaces/roles";

export default class Roles extends ApiClient implements IRoles {

    private _baseUrl: string;
    constructor(private platformSettings: PlatformSettings) {
        super();
        this._baseUrl = platformSettings.apiUrl + "/auth";
    }

    get baseUrl(): string {
        return this._baseUrl;
    }

    public async getRoles(accessToken: string): Promise<ServiceResponse<PagedResults<RoleDto>>> {
        return await super.Get<PagedResults<RoleDto>>(this.baseUrl, "roles", accessToken);
    }
    public async searchRoles(
        page: number,
        pageSize: number,
        name: string,
        accessToken: string): Promise<ServiceResponse<PagedResults<RoleDto>>> {

        let endpoint = `roles?page=${page}&pageSize=${pageSize}`;

        if (name != null) {
            endpoint += `&name=${name}`;
        }

        return await super.Get<PagedResults<RoleDto>>(
            this.baseUrl,
            endpoint,
            accessToken);
    }
    public async getRoleById(roleId: number, accessToken: string): Promise<ServiceResponse<RoleDto>> {
        return await super.Get<RoleDto>(this.baseUrl, `roles/${roleId}`, accessToken);
    }
    public async getRoleByName(roleName: string, accessToken: string): Promise<ServiceResponse<RoleDto>> {
        return await super.Get<RoleDto>(this.baseUrl, `roles/${roleName}`, accessToken);
    }
    public async createRole(dto: CreateRoleDto, accessToken: string): Promise<ServiceResponse<RoleDto>> {
        return await super.Post<CreateRoleDto, RoleDto>(this.baseUrl, "roles", dto, accessToken);
    }
    public async deleteRole(roleId: number, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `roles/${roleId}`, accessToken);
    }
    public async addRoleAuthScope(roleId: number, authScope: string, accessToken: string): Promise<ServiceResponse<RoleDto>> {
        return await super.Put<null, RoleDto>(this.baseUrl, `roles/${roleId}/auth-scopes/${authScope}`, null, accessToken);
    }
    public async removeRoleAuthScope(roleId: number, authScope: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `roles/${roleId}/auth-scopes/${authScope}`, accessToken);
    }
    public async replaceRoleAuthScopes(roleId: number, authScopes: string[], accessToken: string): Promise<ServiceResponse<RoleDto>> {
        return await super.Put<string[], RoleDto>(this.baseUrl, `roles/${roleId}/auth-scopes`, authScopes, accessToken);
    }

    public async getAuthScopes(accessToken: string): Promise<ServiceResponse<Array<string>>> {
        return await super.Get<Array<string>>(this.baseUrl, `auth-scopes`, accessToken);
    }

    public hasScopeAccess(target: UserDto | AppDto, authScope: string): ServiceResponse<boolean> {

        // get a combined list of scopes assigned either directly or via roles
        const roleScopes = target.roles.reduce((scopes, role) => [...scopes, role.authScopes], []).flat();
        let authScopes = target.authScopes.concat(roleScopes).map(scope => scope.toLowerCase());

        // scopes are simply strings that map to a project, class or method within our code,
        // using the convention {AppScope}-{ServiceScope}-{ActionScope}
        let scopeParts = authScope.toLowerCase().split('-');

        let allowedScopes: string[] = [];

        if (scopeParts.length >= 1) {
            allowedScopes.push(scopeParts[0]);
        }

        if (scopeParts.length >= 2) {
            allowedScopes.push(`${scopeParts[0]}-${scopeParts[1]}`);
        }

        if (scopeParts.length >= 3) {
            allowedScopes.push(`${scopeParts[0]}-${scopeParts[1]}-${scopeParts[2]}`);
        }

        var hasAccess = authScopes.some(scope => allowedScopes.includes(scope));
        return ServiceResponseFactory.SuccessfulResponse(hasAccess);
    }
}

export { Roles }